import { useState } from "react";
import {
  Users,
  Truck,
  MapPin,
  IndianRupee,
  TrendingUp,
  Clock,
  FileWarning,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { useDashboardStats, useDashboardCharts } from "../hooks/useDashboard";
import { RevenueBreakdownModal } from "../components/RevenueBreakdownModal";
import { PayoutBalanceCard } from "../components/PayoutBalanceCard";
import { PageHeader } from "@/components/common/PageHeader";
import { StatCard, StatsGrid } from "@/components/common/StatCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SkeletonStats } from "@/components/ui/skeleton";
import { AreaChart } from "@/components/charts/AreaChart";

export function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: chartData } = useDashboardCharts();
  const [revenueOpen, setRevenueOpen] = useState(false);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description="Welcome back! Here's what's happening with your business today."
      />

      {/* Stats Grid */}
      {statsLoading ? (
        <SkeletonStats />
      ) : (
        <StatsGrid>
          <StatCard
            title="Total Customers"
            value={stats?.totalCustomers || 0}
            change={stats?.customerGrowth}
            icon={Users}
            iconColor="text-blue-500"
          />
          <StatCard
            title="Total Drivers"
            value={stats?.totalDrivers || 0}
            change={stats?.driverGrowth}
            subtitle={
              stats && stats.registeredDrivers > stats.totalDrivers
                ? `${stats.totalDrivers} onboarded · ${stats.registeredDrivers} registered`
                : undefined
            }
            icon={Truck}
            iconColor="text-green-500"
          />
          <StatCard
            title="Total Bookings"
            value={stats?.totalBookings || 0}
            change={stats?.bookingGrowth}
            icon={MapPin}
            iconColor="text-purple-500"
          />
          <StatCard
            title="Bookfleet Revenue"
            value={stats?.totalRevenue || 0}
            change={stats?.revenueGrowth}
            icon={IndianRupee}
            iconColor="text-amber-500"
            format="currency"
            onClick={() => setRevenueOpen(true)}
          />
        </StatsGrid>
      )}

      <RevenueBreakdownModal isOpen={revenueOpen} onClose={() => setRevenueOpen(false)} />

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="rounded-full bg-primary/10 p-3">
              <TrendingUp className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Bookings</p>
              <p className="text-2xl font-bold">{stats?.activeBookings || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="rounded-full bg-warning/10 p-3">
              <FileWarning className="h-6 w-6 text-warning" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Pending KYC</p>
              <p className="text-2xl font-bold">{stats?.pendingKYC || 0}</p>
            </div>
            <Link to={ROUTES.KYC_PENDING} className="ml-auto">
              <Button variant="ghost" size="sm">
                Review
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="rounded-full bg-success/10 p-3">
              <Clock className="h-6 w-6 text-success" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                Avg. Delivery Time
              </p>
              <p className="text-2xl font-bold">28 min</p>
            </div>
          </CardContent>
        </Card>
        <PayoutBalanceCard />
      </div>

      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        <AreaChart
          title="Bookings This Week"
          data={chartData?.bookings || []}
          xKey="date"
          yKeys={[{ key: "value", name: "Bookings", color: "#3B82F6" }]}
          height={300}
          showLegend={false}
        />
        <AreaChart
          title="Revenue This Week"
          data={chartData?.revenue || []}
          xKey="date"
          yKeys={[{ key: "value", name: "Revenue", color: "#10B981" }]}
          height={300}
          formatValue="currency"
          showLegend={false}
        />
      </div>
    </div>
  );
}
